import * as R from 'ramda'
import { EquipmentGroup, EquipmentGroupStId, Offer, Price } from 'app/types/types'
import { setOfferData } from './current-offer-slice'
import { getOffersPromise, updateOfferPromise } from 'app/app/(client)/db/db'
import { setUnsavedOfferCount } from './unsaved-offer-slice'
import { setOfferEdited, unsavedOffersCount } from 'app/app/(client)/nemo/offer'
import { wrapCreateAsyncThunk } from 'app/app/(client)/utils/async-handler'
import {
  calculateDiscountedPrice,
  applyDiscountToGroupPricesWithRounding,
  getPricesForCurrentOffer
} from 'app/app/(client)/utils/pricing-summary-utils'
import { setContractIntroduction } from 'app/app/(client)/utils/offer'
import { setMBSConfigInvalid } from '../offering/offering-slice'

type UpdateOffer = (offer: Offer) => Offer

export const updateOfferAsync = wrapCreateAsyncThunk<void, UpdateOffer>(
  'currentOffer/updateOfferAsync',
  async (updater: UpdateOffer, thunkAPI) => {
    const { dispatch, getState } = thunkAPI
    const {
      currentOffer: { data: currentOfferData },
      offeringDetails: { userProfile },
      configdata: { data: configData }
    } = getState()
    const profile = !R.isEmpty(userProfile) ? userProfile : configData?.profile
    if (!currentOfferData) {
      throw new Error('No current offer available')
    }
    const updatedOffer = updater(currentOfferData)
    const offerWithSubSalesOrg =
      profile && updatedOffer.subSalesOrganization !== profile.subSalesOrganization
        ? { ...updatedOffer, subSalesOrganization: profile.subSalesOrganization }
        : updatedOffer

    const editedOffer = setOfferEdited(offerWithSubSalesOrg)
    dispatch(setOfferData(editedOffer))
    await updateOfferPromise(editedOffer)
    const offersFromIDb = await getOffersPromise()
    const unSavedOffers = unsavedOffersCount(offersFromIDb)
    dispatch(setUnsavedOfferCount(unSavedOffers))
  }
)

export const setOfferAsync = wrapCreateAsyncThunk<void, Offer>(
  'currentOffer/setOfferAsync',
  async (offer: Offer, thunkAPI) => {
    const { dispatch } = thunkAPI
    if (!offer) {
      throw new Error('No offer available')
    }
    dispatch(setOfferData(offer))
    await updateOfferPromise(offer)
    const allOffers = await getOffersPromise()
    const count = unsavedOffersCount(allOffers)
    dispatch(setUnsavedOfferCount(count))
  }
)

// async thunk action to update prices in current offer
export const updateOfferPrices = wrapCreateAsyncThunk<
  void,
  { offer: Offer; discounts?: Record<EquipmentGroupStId, number> }
>('currentOffer/updateOfferPrices', async ({ offer, discounts }, thunkAPI) => {
  const { dispatch, getState } = thunkAPI

  const state = getState()
  if (R.isEmpty(offer) || R.isNil(offer)) {
    return
  }

  const { buildings, equipmentGroups, commercialTerms, opportunity, prices } = offer
  if (!equipmentGroups || !buildings || !commercialTerms) {
    return
  }

  const { data: configDataState } = state.configdata

  const { frontLineConfig } = configDataState || {}

  if (!frontLineConfig) {
    return
  }

  const { pricing } = frontLineConfig
  if (!pricing) {
    return
  }

  const { variables: pricingVariables } = pricing
  if (!pricingVariables) {
    return
  }

  // Calculate updated prices using the utility function
  const { equipmentPrice, isValidPrice } = getPricesForCurrentOffer(
    buildings,
    equipmentGroups,
    commercialTerms,
    opportunity,
    frontLineConfig,
    discounts
  )

  const updatedPrices =
    !discounts && Array.isArray(prices)
      ? equipmentPrice.map(price => {
          const existingEquipmentPrice = prices.find(existingPrice => existingPrice.equipmentId === price.equipmentId)
          const existingDiscount = existingEquipmentPrice?.discount

          if (typeof existingDiscount !== 'number') {
            return price
          }

          return {
            ...price,
            discount: existingDiscount,
            discountedPrice: parseFloat(calculateDiscountedPrice(price.equipmentPrice, existingDiscount).toFixed(2))
          }
        })
      : equipmentPrice

  dispatch(
    updateOfferAsync(_ => ({
      ...offer,
      prices: updatedPrices
    }))
  )

  const isValidSelections =
    equipmentGroups &&
    Object.values(equipmentGroups).some(
      (group: EquipmentGroup) =>
        (group?.elevator?.selections?.KCSM_CARE_VISIT && group.elevator.selections.KCSM_CARE_VISIT !== null) ||
        (group?.escalator?.selections?.KCSM_CARE_VISIT && group.escalator.selections.KCSM_CARE_VISIT !== null)
    )

  dispatch(setMBSConfigInvalid(!isValidPrice || !isValidSelections))
})

export const updateOfferGroupDiscounts = wrapCreateAsyncThunk<
  void,
  { discounts: Record<EquipmentGroupStId, number>; groupId?: EquipmentGroupStId }
>('currentOffer/updateOfferGroupDiscounts', async ({ discounts, groupId }, thunkAPI) => {
  const { dispatch } = thunkAPI

  const updatedOfferObj = (currOffer: Offer): Offer => {
    if (!currOffer || !currOffer.prices) {
      return currOffer
    }

    const { prices } = currOffer

    const updatedPricesByEquipmentId = new Map<string, Price>()

    if (groupId) {
      // Update only the specific group
      const groupPrices = prices.filter(price => price.groupId === groupId)
      const groupDiscount = discounts[groupId] ?? 0
      const updatedGroupPrices = applyDiscountToGroupPricesWithRounding(groupPrices, groupDiscount)

      updatedGroupPrices.forEach(price => {
        updatedPricesByEquipmentId.set(price.equipmentId, price)
      })
    } else {
      // Update all groups
      const pricesByGroup = prices.reduce<Record<EquipmentGroupStId, Price[]>>((acc, price) => {
        if (!acc[price.groupId]) {
          acc[price.groupId] = []
        }
        acc[price.groupId].push(price)
        return acc
      }, {})

      Object.entries(pricesByGroup).forEach(([gId, groupPrices]) => {
        const groupDiscount = discounts[gId] ?? 0
        const updatedGroupPrices = applyDiscountToGroupPricesWithRounding(groupPrices, groupDiscount)

        updatedGroupPrices.forEach(price => {
          updatedPricesByEquipmentId.set(price.equipmentId, price)
        })
      })
    }

    const updatedPrices = prices.map(price => updatedPricesByEquipmentId.get(price.equipmentId) || price)

    return {
      ...currOffer,
      prices: updatedPrices
    }
  }

  dispatch(updateOfferAsync(offer => updatedOfferObj(offer)))
})

export const updateOfferTenderInstruction = wrapCreateAsyncThunk<void, string>(
  'currentOffer/updateOfferTenderInstruction',
  async (tenderInstruction, thunkAPI) => {
    const { dispatch, getState } = thunkAPI

    const state = getState()

    const { data: configDataState } = state.configdata
    if (R.isEmpty(configDataState) || R.isNil(configDataState) || !configDataState) {
      return
    }

    const { settings } = configDataState
    if (R.isEmpty(settings) || R.isNil(settings) || !settings) {
      return
    }

    dispatch(updateOfferAsync(offer => setContractIntroduction(settings.language, tenderInstruction)(offer)))
  }
)

export const updateOfferAdditionalDetails = wrapCreateAsyncThunk<void, string>(
  'currentOffer/updateOfferAdditionalDetails',
  async (additionalDetails, thunkAPI) => {
    const { dispatch } = thunkAPI
    dispatch(updateOfferAsync(offer => ({ ...offer, additionalContractDetails: additionalDetails })))
  }
)

export const updateOfferCustomAttachmentComments = wrapCreateAsyncThunk<void, string>(
  'currentOffer/updateOfferCustomAttachmentComments',
  async (customAttachmentComments, thunkAPI) => {
    const { dispatch } = thunkAPI
    dispatch(updateOfferAsync(offer => ({ ...offer, customAttachmentComments })))
  }
)

export const updateOfferAttachments = wrapCreateAsyncThunk<void, number[]>(
  'currentOffer/updateOfferAttachments',
  async (attachmentOption, thunkAPI) => {
    const { dispatch } = thunkAPI
    dispatch(updateOfferAsync(offer => ({ ...offer, attachments: attachmentOption })))
  }
)

export const updateOfferCustomAttachmentSelection = wrapCreateAsyncThunk<void, string[]>(
  'currentOffer/updateOfferCustomAttachmentSelection',
  async (customAttachmentSelection, thunkAPI) => {
    const { dispatch } = thunkAPI
    dispatch(updateOfferAsync(offer => ({ ...offer, customAttachmentSelection })))
  }
)

export const updateWarrantyOptions = wrapCreateAsyncThunk<void, number | undefined>(
  'currentOffer/updateOfferWarranty',
  async (warranty, thunkAPI) => {
    const { dispatch } = thunkAPI
    dispatch(updateOfferAsync(offer => ({ ...offer, warranty })))
  }
)
